<script lang="ts">
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import type { SbTable } from '../types';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import Checkmark from '$lib/icons/checkmark.svelte';
	import MinusV2 from '$lib/icons/minusV2.svelte';
	import Mill from '$lib/logos/mill.svelte';

	interface Props {
		content: ISbStoryData<{
			headline_alignment: 'left' | 'center';
			headline: string;
			table: SbTable;
		}>['content'];
	}

	let { content }: Props = $props();

	let showScrollingShadow = $state(false);

	function handleScroll(event: Event) {
		showScrollingShadow = (event.target as HTMLElement).scrollLeft > 20;
	}
</script>

<section class="my-12 text-brown-1" use:editable={content}>
	<PageGrid16>
		<h1
			class={[
				'col-span-full text-pretty text-[36px]/[112%] tracking-[-.36px] max-lg:mx-5 lg:col-start-3 lg:col-end-[15] lg:text-[48px]/[120%] lg:tracking-[-.48px]',
				{ 'text-center': content.headline_alignment === 'center' }
			]}
		>
			{content.headline}
		</h1>
	</PageGrid16>
	<PageGrid16 gridClass="relative mt-5 lg:mt-16">
		<div
			class="scrollbar-hide col-span-full overflow-x-scroll max-lg:ml-5 max-lg:pr-5 lg:col-start-3 lg:col-end-[15]"
			onscroll={handleScroll}
		>
			<table class="w-full border-separate border-spacing-0">
				<thead>
					<tr>
						{#each content.table.thead as th, index}
							<th
								class={[
									'py-[15px] font-normal',
									index === 0 && 'sticky-column bg-white',
									index === 1 &&
										'green-bottom-border rounded-t-xl bg-green-1  text-[24px]/[108%] tracking-[-.24px] text-white lg:text-[32px]/[110%] lg:tracking-[-.32px]',
									index !== 1 &&
										'bottom-border text-[14px]/[160%] tracking-[-.14px] text-brown-1 lg:text-[20px]/[110%] lg:tracking-[-.2px]'
								]}
							>
								{#if th.value === 'Mill'}
									<div class="flex items-center justify-center">
										<Mill class="h-8 w-8 lg:h-10 lg:w-10" />
									</div>
								{:else}
									{th.value}
								{/if}
							</th>
						{/each}
					</tr>
				</thead>
				<tbody>
					{#each content.table.tbody as tb}
						<tr class="text-center">
							{#each tb.body as entry, index}
								{#if index === 0}
									<th
										class="sticky-column bottom-border bg-white p-0 text-left text-[16px]/[150%] font-normal tracking-[-.16px] max-lg:min-w-[calc(50vw-20px)] lg:w-[33%] lg:text-[20px]/[140%] lg:tracking-[-.2px]"
									>
										<span class="pr-4">
											{entry.value}
										</span>
									</th>
								{:else}
									<td
										class={[
											'border-b py-5 max-lg:min-w-[90px] lg:w-[150px] lg:py-6',
											index === 1 ? 'green-bottom-border' : 'bottom-border',
											index === 1 && 'bg-green-1'
										]}
									>
										{#if entry.value === 'true'}
											<div class="flex items-center justify-center">
												<div
													class={[
														'rounded-full p-1.5 lg:p-2',
														{ 'bg-green-5 text-green-1': index === 1 },
														{ 'bg-brown-5 text-brown-1': index !== 1 }
													]}
												>
													<Checkmark class="h-4 w-4 lg:h-5 lg:w-5" />
												</div>
											</div>
										{:else if entry.value === 'x'}
											<div class="flex items-center justify-center">
												<MinusV2 class="w-4 text-brown-4" />
											</div>
										{:else}
											<div>
												{entry.value}
											</div>
										{/if}
									</td>
								{/if}
							{/each}
						</tr>
					{/each}
				</tbody>
			</table>
		</div>

		<div
			class={[
				'absolute left-0 top-0 z-20 h-full w-1/2 lg:hidden',
				showScrollingShadow && 'right-shadow'
			]}
		></div>
	</PageGrid16>
</section>

<style lang="scss">
	.sticky-column {
		position: sticky;
		left: 0px;
	}

	.right-shadow {
		border-right: 0.5px solid theme('colors.brown.5');
		box-shadow: 4px 0px 8px -6px rgba(0, 0, 0, 0.2);
	}

	.bottom-border {
		border-bottom: 1px solid rgba(0, 0, 0, 0.08);
	}

	.green-bottom-border {
		border-bottom: 1px solid rgb(29, 67, 57);
	}
</style>
